import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery as useZeroQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/hooks/useAuth";

export type NotificationType =
  | "comment_mention"
  | "meeting_note_mention"
  | "task_assigned"
  | "task_updated"
  | "task_due_soon"
  | "task_due_today"
  | "invoice_payment_overdue";

export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  body: string | null;
  entity_type: string | null;
  entity_id: string | null;
  source_comment_id: string | null;
  source_meeting_note_id: string | null;
  source_mention_id: string | null;
  source_task_id: string | null;
  actor_id: string | null;
  is_read: boolean;
  created_at: string;
}

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };
type NotificationsState = {
  data: Notification[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  unreadCount: number;
};

const NotificationsContext = createContext<NotificationsState | null>(null);

type ZeroNotificationRow = {
  id: string;
  userID: string;
  type: NotificationType;
  title: string;
  body?: string | null;
  entityType?: string | null;
  entityID?: string | null;
  sourceCommentID?: string | null;
  sourceMeetingNoteID?: string | null;
  sourceMentionID?: string | null;
  sourceTaskID?: string | null;
  actorID?: string | null;
  isRead: boolean;
  createdAt?: number | string | null;
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyNotification(row: ZeroNotificationRow): Notification {
  return {
    id: row.id,
    user_id: row.userID,
    type: row.type,
    title: row.title,
    body: row.body ?? null,
    entity_type: row.entityType ?? null,
    entity_id: row.entityID ?? null,
    source_comment_id: row.sourceCommentID ?? null,
    source_meeting_note_id: row.sourceMeetingNoteID ?? null,
    source_mention_id: row.sourceMentionID ?? null,
    source_task_id: row.sourceTaskID ?? null,
    actor_id: row.actorID ?? null,
    is_read: row.isRead,
    created_at: toLegacyTimestamp(row.createdAt),
  };
}

async function reportZeroMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

function useNotificationsQuery(): NotificationsState {
  const { user } = useAuth();
  const query = user?.id ? appQueries.notifications.listMine() : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!user?.id });
  const notifications = useMemo(
    () => ((data ?? []) as ZeroNotificationRow[]).map(toLegacyNotification),
    [data],
  );

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.is_read).length,
    [notifications],
  );

  return {
    data: notifications,
    isLoading:
      !!user?.id && details.type !== "complete" && notifications.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
    unreadCount,
  };
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const state = useNotificationsQuery();

  return (
    <NotificationsContext.Provider value={state}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const state = useContext(NotificationsContext);
  if (!state) {
    throw new Error(
      "useNotifications must be used within NotificationsProvider",
    );
  }
  return state;
}

export function useMarkNotificationRead() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (notificationId: string) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.notifications.markRead({ id: notificationId }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Notification update rejected",
      );
      return notificationId;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (notificationId: string, options?: MutationOptions<string>) => {
      void run(notificationId).then((data) => options?.onSuccess?.(data));
    },
    mutateAsync: run,
    isPending,
  };
}

export function useMarkAllNotificationsRead() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async () => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.notifications.markAllRead({}));
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Notifications update rejected",
      );
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (_input?: undefined, options?: MutationOptions<void>) => {
      void run().then((data) => options?.onSuccess?.(data));
    },
    mutateAsync: run,
    isPending,
  };
}
